import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { doc, getFirestore, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import type { PublicResultsClassFS, PublicShowFS } from './types';

/**
 * Same project as apps/ed-usa. A Firebase web config is a public identifier, not
 * a secret — access is decided by security rules, which make `publicResults` and
 * `publicShows` read-only and expose nothing else to this app.
 */
const firebaseConfig = {
  apiKey: 'AIzaSyAEF12qszjesvPGOs1hZPQM-I8JwLEsuBI',
  authDomain: 'ecuestredigital.firebaseapp.com',
  projectId: 'ecuestredigital',
  storageBucket: 'ecuestredigital.appspot.com',
  messagingSenderId: '581815221685',
  appId: '1:581815221685:web:3c43ec9a794d72edf56dfc',
};

/**
 * reCAPTCHA Enterprise site key, shared with apps/ed-usa.
 *
 * App Check is the reason this widget is served in an iframe from our own origin
 * rather than injected into the host page: a reCAPTCHA site key is bound to its
 * domains, so an SDK running on a partner's origin could never attest. Inside an
 * iframe we serve, it can — which is what keeps a lifted config from being used
 * to drain read quota.
 *
 * The widget's hosting domain must be registered on this key. Until it is,
 * attestation fails; that is survivable while enforcement is off, so failure is
 * logged and the widget continues rather than showing an error to spectators.
 */
const RECAPTCHA_SITE_KEY = '6LeBxv4sAAAAANey0MvWGjGL5lg6-kssJ8anXsOs';

const app = initializeApp(firebaseConfig);

try {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
} catch (err) {
  console.warn('[results-widget] App Check unavailable, continuing unattested', err);
}

const db = getFirestore(app);

/** Accepts `123` or `us_123` so an embed code can use either form. */
export function normalizeId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.startsWith('us_') ? trimmed : `us_${trimmed}`;
}

export function watchClass(
  classId: string,
  onData: (data: PublicResultsClassFS | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'publicResults', classId),
    (snapshot) => onData(snapshot.exists() ? (snapshot.data() as PublicResultsClassFS) : null),
    onError,
  );
}

export function watchShow(
  showId: string,
  onData: (data: PublicShowFS | null) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'publicShows', showId),
    (snapshot) => onData(snapshot.exists() ? (snapshot.data() as PublicShowFS) : null),
    onError,
  );
}
